/**
 * @fileoverview Core module system type definitions and constants
 * 
 * This file contains all the JSDoc type definitions and constants that define
 * the module system architecture for ParrotMate's modular components.
 */

/**
 * @typedef {Object} ModuleConfig
 * @property {string} id - Unique identifier for the module
 * @property {string} name - Human-readable name of the module
 * @property {string} version - Semantic version of the module
 * @property {'user'|'admin'|'shared'} context - Context where the module can be used
 * @property {string[]} permissions - Required permissions to use this module
 * @property {string[]} [dependencies] - Optional module dependencies
 * @property {string} [description] - Module description for documentation
 * @property {string} [author] - Module author information
 * @property {string[]} [tags] - Module tags for categorization
 */

/**
 * @typedef {Object} UserContext
 * @property {string} id - User ID
 * @property {string} email - User email
 * @property {string} [name] - User display name
 * @property {'user'|'admin'} role - User role
 * @property {string} [organizationId] - Organization ID if applicable
 * @property {Object} [preferences] - User preferences
 */

/**
 * @typedef {Object} Permission
 * @property {string} id - Permission identifier
 * @property {string} name - Permission name
 * @property {string} [description] - Permission description
 * @property {string} resource - Resource this permission applies to
 * @property {string[]} actions - Actions allowed by this permission
 */

/**
 * @typedef {Object} ApiClient
 * @property {function(string, Object=): Promise<*>} get - GET request method
 * @property {function(string, *, Object=): Promise<*>} post - POST request method
 * @property {function(string, *, Object=): Promise<*>} put - PUT request method
 * @property {function(string, Object=): Promise<*>} delete - DELETE request method
 * @property {function(string, *, Object=): Promise<*>} patch - PATCH request method
 */

/**
 * @typedef {Object} ThemeContext
 * @property {'light'|'dark'} mode - Current theme mode
 * @property {Object<string, string>} colors - Theme colors
 * @property {Object} typography - Typography settings
 * @property {Object<string, number>} spacing - Spacing values
 * @property {Object<string, number>} breakpoints - Breakpoints for responsive design
 */

/**
 * @typedef {Object} ToastContext
 * @property {function(string, Object=): void} success - Show success toast
 * @property {function(string, Object=): void} error - Show error toast
 * @property {function(string, Object=): void} warning - Show warning toast
 * @property {function(string, Object=): void} info - Show info toast
 */

/**
 * @typedef {Object} ModuleContext
 * @property {UserContext} user - Current user information
 * @property {Permission[]} permissions - User permissions
 * @property {ApiClient} apiClient - API client for backend communication
 * @property {ThemeContext} theme - Theme context for styling
 * @property {ToastContext} toast - Toast notifications
 * @property {Object} [registry] - Module registry for accessing other modules
 * @property {ModuleEventBus} [eventBus] - Event bus for inter-module communication
 */

/**
 * @typedef {Object} ModuleEvent
 * @property {string} type - Event type identifier
 * @property {*} payload - Event payload data
 * @property {string} source - Source module ID
 * @property {number} timestamp - Event timestamp
 * @property {string} [target] - Optional target module ID
 * @property {Object} [metadata] - Event metadata
 */

/**
 * @typedef {Object} ModuleProps
 * @property {*} config - Module-specific configuration
 * @property {ModuleContext} context - Shared module context
 * @property {function(ModuleEvent): void} [onEvent] - Event handler for module events
 * @property {string} [className] - Optional CSS class name
 * @property {Object} [style] - Optional inline styles
 * @property {string} [testId] - Optional test ID for testing
 */

/**
 * @typedef {Object} ModuleRegistryEntry
 * @property {string} id - Module ID
 * @property {string} name - Module name
 * @property {string} version - Module version
 * @property {React.ComponentType<ModuleProps>} component - React component for the module
 * @property {ModuleConfig} config - Module configuration
 * @property {string[]} dependencies - Module dependencies
 * @property {Date} [loadedAt] - Timestamp when module was loaded
 * @property {'loaded'|'loading'|'error'|'unloaded'} status - Module loading status
 * @property {string} [error] - Error information if status is 'error'
 */

/**
 * @typedef {Object} ModuleState
 * @property {string} id - Module ID
 * @property {*} config - Module configuration
 * @property {*} data - Module data
 * @property {boolean} loading - Loading state
 * @property {string} [error] - Error state
 * @property {Date} lastUpdated - Last update timestamp
 * @property {ModuleEvent[]} events - Module events history
 */

/**
 * @typedef {Object} ModuleError
 * @property {ModuleErrorType} type - Error type classification
 * @property {string} message - Human-readable error message
 * @property {string} code - Error code for programmatic handling
 * @property {*} [details] - Additional error details
 * @property {boolean} recoverable - Whether the error can be recovered from
 * @property {boolean} retryable - Whether the operation can be retried
 * @property {string} [moduleId] - Module ID where the error occurred
 * @property {string} [stack] - Stack trace if available
 * @property {number} [timestamp] - Timestamp when error occurred
 */

/**
 * @typedef {Object} ModuleLifecycle
 * @property {function(): void|Promise<void>} [onMount] - Called when module is mounted
 * @property {function(): void|Promise<void>} [onUnmount] - Called when module is unmounted
 * @property {function(*, *): void|Promise<void>} [onConfigChange] - Called when module config changes
 * @property {function(ModuleContext, ModuleContext): void|Promise<void>} [onContextChange] - Called when module context changes
 */

/**
 * Module error types for classification
 * @readonly
 * @enum {string}
 */
export const ModuleErrorType = {
  CONFIGURATION_ERROR: 'configuration_error',
  PERMISSION_ERROR: 'permission_error',
  NETWORK_ERROR: 'network_error',
  VALIDATION_ERROR: 'validation_error',
  SYNC_ERROR: 'sync_error',
  STORAGE_ERROR: 'storage_error',
  DEPENDENCY_ERROR: 'dependency_error',
  RUNTIME_ERROR: 'runtime_error'
};

/**
 * Module contexts for categorization
 * @readonly
 * @enum {string}
 */
export const ModuleContextType = {
  USER: 'user',
  ADMIN: 'admin',
  SHARED: 'shared'
};

/**
 * Module status values
 * @readonly
 * @enum {string}
 */
export const ModuleStatus = {
  LOADED: 'loaded',
  LOADING: 'loading',
  ERROR: 'error',
  UNLOADED: 'unloaded'
};

/**
 * Default module configuration
 * @type {Partial<ModuleConfig>}
 */
export const DEFAULT_MODULE_CONFIG = {
  version: '1.0.0',
  context: ModuleContextType.SHARED,
  permissions: [],
  dependencies: [],
  tags: []
};

/**
 * Module event types
 * @readonly
 * @enum {string}
 */
export const ModuleEventType = {
  MODULE_LOADED: 'module_loaded',
  MODULE_UNLOADED: 'module_unloaded',
  MODULE_ERROR: 'module_error',
  MODULE_CONFIG_CHANGED: 'module_config_changed',
  MODULE_DATA_CHANGED: 'module_data_changed',
  MODULE_SYNC_STARTED: 'module_sync_started',
  MODULE_SYNC_PROGRESS: 'module_sync_progress',
  MODULE_SYNC_COMPLETED: 'module_sync_completed',
  MODULE_SYNC_FAILED: 'module_sync_failed',
  MODULE_SYNC_RETRY_SCHEDULED: 'module_sync_retry_scheduled',
  MODULE_SYNC_RETRY_SUCCESS: 'module_sync_retry_success',
  MODULE_SYNC_RETRY_EXHAUSTED: 'module_sync_retry_exhausted',
  USER_ACTION: 'user_action',
  SYSTEM_EVENT: 'system_event'
};

/**
 * @typedef {Object} ModuleEventBus
 * @property {function(ModuleEvent): void} emit - Emit an event
 * @property {function(string, function): function} subscribe - Subscribe to events
 * @property {function(function): void} unsubscribe - Unsubscribe from events
 * @property {function(): void} clear - Clear all subscriptions
 */
